<template>
  <FullCalendar :options="calendarOptions" ref="fullCalendar" />
</template>

<script>
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { mapActions, mapState } from "vuex";
import { getFilteredEvents } from "./../api";
import moment from "moment";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";

export default {
  name: "MiniCalendarMonthView",
  components: { FullCalendar },
  props: {
    showDate: String,
  },
  data() {
    return {
      events: [],
      selectedStartDate: null,
      selectedEndDate: null,
      expandedDays: new Set(), // Track expanded days
      hideCalendarGrid: false,
      previousViewStart: null,
    };
  },
  computed: {
    ...mapState(["date_range", "eventlist", "navigationDirection"]),

    formattedEvents() {
      const eventsByDate = {};
      this.eventlist.forEach((event) => {
        const eventStart = event.date?.start?.date || "";
        const eventEndRaw = event.date?.end?.date || eventStart;
        const isMultiDay = eventStart !== eventEndRaw;
        const eventEnd = isMultiDay
        ? moment(eventEndRaw).add(1, "day").format("YYYY-MM-DD")
        : eventEndRaw;
        const time = event.date?.start?.time || "";
        if (!eventsByDate[eventStart]) {
          eventsByDate[eventStart] = { singleDay: [], multiDay: [] };
        }

        const eventData = {
          id: event.id || `event-${Math.random()}`,
          title: event.title,
          start: eventStart,
          time: time,
          end: eventEnd,
          url: event.url || "#",
          isMultiDay,
          extendedProps: {
            isMultiDay,
            time: event.date?.start?.time || "",
            all_day: event.date?.all_day || "0", // ensure string for consistency
          },
        };
        if (isMultiDay) {
          eventsByDate[eventStart].multiDay.push(eventData);
        } else {
          eventsByDate[eventStart].singleDay.push(eventData);
        }
      });

      return eventsByDate;
    },

    calendarOptions() {
      return {
        plugins: [dayGridPlugin, interactionPlugin, resourceTimelinePlugin],
        initialView: "dayGridMonth",
        initialDate: this.showDate || new Date(),
        events: this.getLimitedEvents(),
        eventClick: this.openEvent,
        editable: false,
        selectable: false,
        height: "auto",
        datesSet: this.handleMonthChange,
        eventContent: this.renderEventContent, // Custom rendering function
        eventOrder: true, // Disable event sorting
        schedulerLicenseKey: "0966966126-fcs-1742821718",
        dayHeaderContent: function (arg) {
          return {
            html: `<span class="fc-col-header-cell-cushion">${arg.text}</span>`,
          };
        },
        eventBackgroundColor: "transparent",
        eventBorderColor: "transparent",
        headerToolbar: {
          left: "prev",
          center: "title",
          right: "next",
        },
      };
    },
  },

  methods: {
    ...mapActions([
      "setEventList",
      "setLoading",
      "setSelectedMonth",
      "setDateRange",
      "setDateManuallySelected",
      "setNavigationDirection",
    ]),

    ensureCalendarAccessibility() {
      this.$nextTick(() => {
        const calendarEl = this.$refs.fullCalendar?.$el;
        if (!calendarEl) return;
        //Remove problematic/incorrect roles
        calendarEl.querySelectorAll("[role='presentation']").forEach((el) => {
          el.removeAttribute("role");
        });
        //Remove role="grid" if not needed
        const gridTable = calendarEl.querySelector("table[role='grid']");
        if (gridTable) {
          gridTable.removeAttribute("role");
        }

      });
    },
    fixToolbarIconsAccessibility() {
      this.$nextTick(() => {
        const calendarEl = this.$refs.fullCalendar?.$el;
        if (!calendarEl) return;
        const prevIcon = calendarEl.querySelector('.fc-prev-button .fc-icon');
        if (prevIcon) {
          prevIcon.setAttribute('aria-hidden', 'true');
        }
        const nextIcon = calendarEl.querySelector('.fc-next-button .fc-icon');
        if (nextIcon) {
          nextIcon.setAttribute('aria-hidden', 'true');
        }
      });
    },
    openEvent(eventInfo) {
      if (eventInfo.event.url) {
        window.open(eventInfo.event.url);
        eventInfo.jsEvent.preventDefault();
      }
    },

    toggleExpandDay(date) {
      if (this.expandedDays.has(date)) {
        this.expandedDays.delete(date); // Collapse the day
      } else {
        this.expandedDays.add(date); // Expand the day
      }
      // re-render the calendar after Vue and DOM update
      this.$nextTick(() => {
        this.updateCalendarView();
      });

    },

    async fetchEventsForMonth(startDate, endDate, realMonthStart = null, realMonthEnd = null) {
      this.setLoading(true);
      try {
        const middleMonths = this.getMiddleMonths(startDate, endDate);
        const allEvents = [];
        const searchTerm = this.$store.getters.search || "";
        const queryString = Object.entries(
          this.$store.state.selectedCategories || {}
        )
          .flatMap(([key, terms]) => terms.map((term) => `${key}=${term}`))
          .join("&");
        const filtersQueryString = queryString || "";
        for (const monthRange of middleMonths) {
          const response = await getFilteredEvents(
            monthRange.start,
            monthRange.end,
            filtersQueryString,
            1,
            searchTerm,
            100000
          );

          if (response?.data) {
            allEvents.push(...response.data);
          }
        }
        this.selectedStartDate = startDate;
        this.selectedEndDate = endDate;

        this.setEventList({ eventlist: allEvents });

        this.events = allEvents.map((event) => ({
          id: event.id || `event-${Math.random()}`,
          title: event.title,
          start: event.date?.start?.date || "",
          time: event.date?.start?.time || "",
          end: event.date?.end?.date || event.date?.start?.date || "",
          url: event.url || "#",
        }));
        this.$nextTick(() => {
          this.updateCalendarView();
        });
        const checkStart = realMonthStart || startDate;
        const checkEnd = realMonthEnd || endDate;
        //this is for sorry message
        this.checkIfMonthHasEvents(checkStart, checkEnd);
      } catch (error) {
        console.log("error");
      } finally {
        this.setLoading(false);
      }
    },

    checkIfMonthHasEvents(startDate, endDate) {
      const monthEvents = this.eventlist.filter((event) => {
        const eventDate = moment(event.date?.start?.date);
        return eventDate.isSameOrAfter(startDate) && eventDate.isSameOrBefore(endDate);
      });
      this.$emit("update:noEvents", monthEvents.length === 0);
      const hasEvents = monthEvents.length > 0;
      this.hideCalendarGrid = !hasEvents;
      this.$nextTick(() => {
        const calendarEl = this.$refs.fullCalendar?.$el;
        if (calendarEl) {
          const gridBody = calendarEl.querySelector(".fc-daygrid-body");
          const collgrid  = calendarEl.querySelector(".fc-col-header");
          if (gridBody) {
            gridBody.style.display = this.hideCalendarGrid ? "none" : "";
          }
          if (collgrid) {
            collgrid.style.display = this.hideCalendarGrid ? "none" : "";
          }
          const scrollgrid = calendarEl.querySelector(".fc-scrollgrid");
          if (scrollgrid) {
            scrollgrid.style.border = this.hideCalendarGrid ? "none" : "";
          }
        }
      });
    },
    updateCalendarView() {
      if (this.$refs.fullCalendar) {
        const calendarApi = this.$refs.fullCalendar.getApi();
        calendarApi.removeAllEvents();
        calendarApi.addEventSource(this.getLimitedEvents());
        // Also re-check event presence to get sorry message
        const checkStart = this.selectedStartDate || moment().startOf("month").format("YYYY-MM-DD");
        const checkEnd = this.selectedEndDate || moment().endOf("month").format("YYYY-MM-DD");
        this.checkIfMonthHasEvents(checkStart, checkEnd);
        this.ensureCalendarAccessibility();
      }
    },

    handleMonthChange(info) {
      const startDate = moment(info.start).format("YYYY-MM-DD");
      const endDate = moment(info.end).format("YYYY-MM-DD");
      const currentDate = moment(info.start).add(15, "days");
      const selectedMonth = currentDate.format("YYYY-MM");
      const realStartDate = currentDate.clone().startOf("month").format("YYYY-MM-DD");
      const realEndDate = currentDate.clone().endOf("month").format("YYYY-MM-DD");
      //this is for  hiding date range
      if (this.previousViewStart) {
        const prev = moment(this.previousViewStart);
        const curr = moment(startDate);
        if (curr.isAfter(prev)) {
          this.setDateManuallySelected(false);
          this.setNavigationDirection("monthchange");
        } else if (curr.isBefore(prev)) {
          this.setDateManuallySelected(false);
          this.setNavigationDirection("monthchange");
        }
      }
      // Update previous view state
      this.previousViewStart = startDate;
      this.setSelectedMonth(selectedMonth);
      this.setDateRange({
        start: startDate,
        end: endDate,
        type: "custom_range",
      });
      if (startDate !== this.selectedStartDate) {
        this.fetchEventsForMonth(startDate, endDate, realStartDate, realEndDate);
      }
    },

    getMiddleMonths(startDate, endDate) {
      const start = moment(startDate);
      const end = moment(endDate);
      const middleMonths = [];
      while (start.isBefore(end)) {
        middleMonths.push({
          start: start.clone().startOf("month").format("YYYY-MM-DD"),
          end: start.clone().endOf("month").format("YYYY-MM-DD"),
        });
        start.add(1, "month");
      }

      return middleMonths;
    },

    getLimitedEvents() {
      const limitedEvents = [];

      Object.keys(this.formattedEvents).forEach((date) => {
        const { singleDay, multiDay } = this.formattedEvents[date];
        // Always show multi-day events first (Do NOT count these in "+X more")
        limitedEvents.push(...multiDay);
        // Check if the day is expanded
        if (this.expandedDays.has(date)) {
          // If expanded, show all single-day events
          limitedEvents.push(...singleDay);
        } else {
          // If not expanded, show only the first 3 single-day events
          const visibleSingleDayEvents = singleDay.slice(0, 3);
          limitedEvents.push(...visibleSingleDayEvents);
          // Add "+X more" button if there are more than 3 events
          if (singleDay.length > 3) {
            limitedEvents.push({
              id: `more-${date}`,
              title: `+${singleDay.length - 3} more`,
              start: date,
              classNames: ["fc-more-events"],
              extendedProps: { type: "more-events", allEvents: singleDay },
            });
          }
        }
      });
      return limitedEvents;
    },

    renderEventContent(arg) {
      const { event } = arg;
      const isMoreButton = event.extendedProps.type === "more-events";
      const isMultiDay = event.extendedProps.isMultiDay;
      const isAllDay = event.extendedProps.all_day === "1";

      if (isMoreButton) {
        const button = document.createElement("button");
        button.className = "fc-more-events";
        button.textContent = event.title;
        button.addEventListener("click", () =>
          this.toggleExpandDay(event.startStr)
        );
        return { domNodes: [button] };
      }

      const showTime = !isMultiDay && !isAllDay && event.extendedProps.time;
      const timeHtml = showTime
        ? `<span class="fc-event-time">${event.extendedProps.time}</span>`
        : "";

      return {
        html: `<span class="fc-event-title">${event.title}</span>${timeHtml}`,
      };
    },
  },

  watch: {
    eventlist() {
      this.$nextTick(() => {
          this.updateCalendarView();
      });
    },
  },

  mounted() {
    const startOfMonth = moment().startOf("month").format("YYYY-MM-DD");
    const endOfMonth = moment().endOf("month").format("YYYY-MM-DD");
    const realStartDate  = startOfMonth;
    const realEndDate = endOfMonth;
    this.fetchEventsForMonth(startOfMonth, endOfMonth, realStartDate, realEndDate);
    this.ensureCalendarAccessibility();
    this.fixToolbarIconsAccessibility();
  },
};
</script>

<style></style>
